import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

const translations = {
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.settings': 'Settings',
    'nav.logout': 'Logout',
    'nav.projects': 'Projects',
    'nav.admin': 'Admin Panel',
    
    // Auth
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.username': 'Username',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.loginTitle': 'Welcome Back',
    'auth.registerTitle': 'Create Account',
    'auth.loginSubtitle': 'Sign in to continue to WPLMS TTM',
    'auth.registerSubtitle': 'Start creating fine-tuning data',
    
    // Dashboard
    'dashboard.title': 'My Projects',
    'dashboard.newProject': 'New Project',
    'dashboard.noProjects': 'No projects yet',
    'dashboard.noProjectsDesc': 'Create your first project to start generating fine-tuning data',
    'dashboard.samples': 'samples',
    'dashboard.lastUpdated': 'Last updated',
    
    // Editor
    'editor.systemPrompt': 'System Prompt (Role Definition)',
    'editor.systemPromptPlaceholder': 'e.g., You are a humorous tour guide...',
    'editor.samples': 'Conversation Samples',
    'editor.addSample': 'Add Sample',
    'editor.aiFill': 'AI Auto-Fill',
    'editor.export': 'Export JSONL',
    'editor.simulate': 'Test Simulation',
    'editor.userInput': 'User Input',
    'editor.assistantResponse': 'Assistant Response',
    'editor.delete': 'Delete',
    'editor.sampleCount': 'Sample Count',
    'editor.minWarning': 'Need at least 10 samples to export and simulate',
    'editor.goodCount': 'Good! You can export and simulate',
    'editor.recommendMore': 'Recommended: add more samples for better results',
    'editor.generating': 'Generating...',
    'editor.generate10': 'Generate 10',
    'editor.generate20': 'Generate 20',
    'editor.projectName': 'Project Name',
    'editor.description': 'Description',
    
    // Playground
    'playground.title': 'Simulation Playground',
    'playground.subtitle': 'Test how your fine-tuned model would respond',
    'playground.temperature': 'Temperature',
    'playground.send': 'Send',
    'playground.placeholder': 'Type a message to test...',
    'playground.thinking': 'Thinking...',
    'playground.noSamples': 'Add at least 10 samples to use the playground',
    
    // Admin
    'admin.title': 'Admin Settings',
    'admin.systemSettings': 'System Settings',
    'admin.systemName': 'System Name',
    'admin.allowRegistration': 'Allow Registration',
    'admin.aiSettings': 'AI Settings',
    'admin.apiKey': 'OpenAI API Key',
    'admin.apiKeyPlaceholder': 'sk-...',
    'admin.defaultModel': 'Default Model',
    'admin.userManagement': 'User Management',
    'admin.save': 'Save Settings',
    'admin.users': 'Users',
    'admin.role': 'Role',
    'admin.status': 'Status',
    'admin.actions': 'Actions',
    'admin.admin': 'Admin',
    'admin.user': 'User',
    'admin.active': 'Active',
    'admin.inactive': 'Inactive',
    
    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.confirm': 'Confirm',
    'common.back': 'Back',
  },
  zh: {
    // Navigation
    'nav.dashboard': '控制台',
    'nav.settings': '设置',
    'nav.logout': '退出登录',
    'nav.projects': '项目',
    'nav.admin': '管理后台',
    
    // Auth
    'auth.login': '登录',
    'auth.register': '注册',
    'auth.email': '邮箱',
    'auth.password': '密码',
    'auth.username': '用户名',
    'auth.noAccount': '还没有账号？',
    'auth.hasAccount': '已有账号？',
    'auth.loginTitle': '欢迎回来',
    'auth.registerTitle': '创建账号',
    'auth.loginSubtitle': '登录以继续使用 WPLMS TTM',
    'auth.registerSubtitle': '开始创建微调数据',
    
    // Dashboard
    'dashboard.title': '我的项目',
    'dashboard.newProject': '新建项目',
    'dashboard.noProjects': '暂无项目',
    'dashboard.noProjectsDesc': '创建你的第一个项目，开始生成微调数据',
    'dashboard.samples': '条样本',
    'dashboard.lastUpdated': '最后更新',
    
    // Editor
    'editor.systemPrompt': '系统提示词（角色定义）',
    'editor.systemPromptPlaceholder': '例如：你是一个幽默的导游...',
    'editor.samples': '对话样本',
    'editor.addSample': '添加样本',
    'editor.aiFill': 'AI 自动填充',
    'editor.export': '导出 JSONL',
    'editor.simulate': '模拟对话',
    'editor.userInput': '用户输入',
    'editor.assistantResponse': '助手回复',
    'editor.delete': '删除',
    'editor.sampleCount': '样本数量',
    'editor.minWarning': '需要至少10条样本才能导出和模拟',
    'editor.goodCount': '达标！可以导出和模拟',
    'editor.recommendMore': '建议：继续增加样本以提升效果',
    'editor.generating': '生成中...',
    'editor.generate10': '生成10条',
    'editor.generate20': '生成20条',
    'editor.projectName': '项目名称',
    'editor.description': '项目描述',
    
    // Playground
    'playground.title': '模拟演练场',
    'playground.subtitle': '测试微调后模型的回复效果',
    'playground.temperature': '随机度',
    'playground.send': '发送',
    'playground.placeholder': '输入消息进行测试...',
    'playground.thinking': '思考中...',
    'playground.noSamples': '添加至少10条样本才能使用模拟器',
    
    // Admin
    'admin.title': '管理设置',
    'admin.systemSettings': '系统设置',
    'admin.systemName': '系统名称',
    'admin.allowRegistration': '开放注册',
    'admin.aiSettings': 'AI 设置',
    'admin.apiKey': 'OpenAI API Key',
    'admin.apiKeyPlaceholder': 'sk-...',
    'admin.defaultModel': '默认模型',
    'admin.userManagement': '会员管理',
    'admin.save': '保存设置',
    'admin.users': '用户',
    'admin.role': '角色',
    'admin.status': '状态',
    'admin.actions': '操作',
    'admin.admin': '管理员',
    'admin.user': '用户',
    'admin.active': '正常',
    'admin.inactive': '禁用',
    
    // Common
    'common.save': '保存',
    'common.cancel': '取消',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.loading': '加载中...',
    'common.error': '错误',
    'common.success': '成功',
    'common.confirm': '确认',
    'common.back': '返回',
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'zh';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'zh' : 'en');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
